import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, mergeMap, filter, map } from 'rxjs/operators';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { locale as navigationEnglish } from 'assets/i18n/en';

import { UserService } from './main/core/services/user.service';
import { ClientContextService } from './main/core/services/client-context.service';
import { AuthenticationService } from './main/core/services/authentication.service';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;

    // Private
    private _unsubscribeAll: Subject<any>;
    private _currentTitle = '';
    private _currentMetaDescription = '';

    /**
     * 
     * @param document 
     * @param _fuseConfigService 
     * @param _fuseNavigationService 
     * @param _fuseSidebarService 
     * @param _fuseSplashScreenService 
     * @param _fuseTranslationLoaderService 
     * @param _translateService 
     * @param _platform 
     * @param _userService 
     * @param _titleService 
     * @param _activatedRoute 
     * @param _meta 
     * @param _router 
     * @param _clientContextService 
     * @param _authenticationService 
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private _userService: UserService,
        private _titleService: Title,
        private _activatedRoute: ActivatedRoute,
        private _meta: Meta,
        private _router: Router,
        private _clientContextService: ClientContextService,
        private _authenticationService: AuthenticationService

    ) {
        // Set the private defaults
        this._unsubscribeAll = new Subject();

        // Handle App Level titles here
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this._activatedRoute),
            map(route => {
                while (route.firstChild) {
                    route = route.firstChild;
                }
                return route;
            }),
            filter(route => route.outlet === 'primary'),
            mergeMap(route => route.data))
            .subscribe((event) => { this._setTitleAndMetaDescription(false, event['title'], event['description']); });

        this._userService.currentUser
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(user => {
                if (user) {

                    this.navigation = this._userService.getCurrentUserNavigations();

                    // Register the navigation to the service
                    this._fuseNavigationService.unregister('main');
                    this._fuseNavigationService.register('main', this.navigation);

                    // Set the main navigation as our current navigation
                    this._fuseNavigationService.setCurrentNavigation('main');

                }

            });


        // Add languages
        this._translateService.addLangs(['en']);

        // Set the default language
        this._translateService.setDefaultLang('en');

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish);

        // Use a language
        this._translateService.use('en');

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }

        this._clientContextService.isUserAuthorized()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isUserAuthorized) => {
                // this._logger.log('isUserAuthorized', isUserAuthorized);
                // if the user is not authorized/ authenticated then log him/ her out of this system
                if (!isUserAuthorized) {
                    this._authenticationService.logout();
                }
                // else just reset the idle timeout for inactivity
                /* else {
                    this._idleTimeoutService.resetIdleTimeout();
                } */
            });

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                }
                else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for (const className of this.document.body.classList) {
                    if (className && className.toString().startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }


    private _setTitleAndMetaDescription(canUseOld: boolean, title: any = null, metaDescription: any = null): void {
        if (!canUseOld) {
            this._currentTitle = title;
        }
        this._titleService.setTitle(`CBCIS - ${this._currentTitle || ''}`);

        if (metaDescription) {
            this._currentMetaDescription = metaDescription;
            this._meta.updateTag({ name: 'description', content: `${this._currentMetaDescription || ''}` });
        }

    }

}
